/**
 * Network-Aware Hook
 * Detects slow network connections and provides optimization hints
 * Uses Network Information API where available
 */

import { useEffect, useState } from 'react';

type ConnectionType = 'slow-2g' | '2g' | '3g' | '4g' | 'fast' | 'unknown';
type DataSaverMode = boolean;

interface NetworkInfo {
  effectiveType: ConnectionType;
  saveData: DataSaverMode;
  downlink?: number; // Mbps
  rtt?: number; // Round-trip time in ms
  isSlowConnection: boolean;
}

type NavigatorConnection = {
  effectiveType?: ConnectionType;
  saveData?: DataSaverMode;
  downlink?: number;
  rtt?: number;
  addEventListener?: (type: string, listener: () => void) => void;
  removeEventListener?: (type: string, listener: () => void) => void;
};

type NavigatorWithConnection = Navigator & {
  connection?: NavigatorConnection;
  mozConnection?: NavigatorConnection;
  webkitConnection?: NavigatorConnection;
};

/**
 * Hook to detect and respond to network conditions
 * Useful for:
 * - Disabling auto-play videos on slow connections
 * - Reducing image quality
 * - Deferring non-critical requests
 * - Showing offline indicators
 */
export function useNetworkAware(): NetworkInfo {
  const [networkInfo, setNetworkInfo] = useState<NetworkInfo>({
    effectiveType: 'unknown',
    saveData: false,
    isSlowConnection: false,
  });

  useEffect(() => {
    let connection: NavigatorConnection | undefined;
    if (typeof navigator !== 'undefined') {
      const navigatorWithConn = navigator as NavigatorWithConnection;
      connection =
        navigatorWithConn.connection ||
        navigatorWithConn.mozConnection ||
        navigatorWithConn.webkitConnection;
    }

    const updateNetworkInfo = () => {
      if (!connection) {
        // Fallback: Assume fast connection if API not available
        setNetworkInfo({
          effectiveType: 'fast',
          saveData: false,
          isSlowConnection: false,
        });
        return;
      }

      const effectiveType = connection.effectiveType || 'unknown';
      const saveData = connection.saveData || false;
      const downlink = connection.downlink;
      const rtt = connection.rtt;

      // Consider connection slow if:
      // - effectiveType is 2g or slow-2g
      // - RTT > 400ms
      // - downlink < 1 Mbps
      // - saveData mode is enabled
      const isSlowConnection =
        effectiveType === 'slow-2g' ||
        effectiveType === '2g' ||
        (rtt && rtt > 400) ||
        (downlink && downlink < 1) ||
        saveData;

      setNetworkInfo({
        effectiveType,
        saveData,
        downlink,
        rtt,
        isSlowConnection,
      });
    };

    // Initial check
    updateNetworkInfo();

    // Listen for connection changes
    if (!connection?.addEventListener) {
      return undefined; // No cleanup needed if connection API not available
    }

    connection.addEventListener('change', updateNetworkInfo);
    return () => {
      connection?.removeEventListener?.('change', updateNetworkInfo);
    };
  }, []);

  return networkInfo;
}

/**
 * Hook to check if user is online
 */
export function useOnlineStatus(): boolean {
  // The server cannot know the browser's connectivity. Start from the same
  // value on both sides, then reconcile after hydration to keep the root tree
  // structurally stable.
  const [isOnline, setIsOnline] = useState(true);

  useEffect(() => {
    const updateOnlineStatus = () => setIsOnline(navigator.onLine);

    updateOnlineStatus();
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, []);

  return isOnline;
}
